/**
 * SOGI CRM — Standardized error humanizer.
 *
 * Goal: never show users a raw HTTP error or stack trace. Always a clear,
 * actionable French message they can do something about.
 *
 * Use directly:
 *   try { await api.call(); }
 *   catch (e) { toast.error(humanizeError(e)); }
 *
 * Or via the wrapped variant that retries on network errors :
 *   const safeCall = wrapApi(api.call, { retry: true });
 */

/**
 * Convert any thrown value into a short, actionable French message.
 */
export function humanizeError(error) {
  if (!error) return "Une erreur inattendue est survenue.";

  // Browser offline
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "Vous êtes hors-ligne. Vos modifications seront retentées dès la reconnexion.";
  }

  // Axios style: error.response (with status), error.request (no response), error.message
  const resp = error.response;
  const code = resp?.status;
  const detail = resp?.data?.detail || resp?.data?.error?.message;

  // Network error (no response received)
  if (!resp && error.message?.includes("Network")) {
    return "Connexion au serveur impossible. Vérifiez votre Internet puis réessayez.";
  }

  // Custom backend error contract: { error: { code, message } }
  if (resp?.data?.error?.message) {
    return resp.data.error.message;
  }

  // FastAPI validation errors: detail is an array
  if (Array.isArray(detail)) {
    const msg = detail
      .map((d) => d?.msg || JSON.stringify(d))
      .filter(Boolean)
      .join(" · ");
    return msg || "Le formulaire contient des champs invalides.";
  }

  if (typeof detail === "string" && detail.length < 200) {
    return detail;
  }

  switch (code) {
    case 400: return "Requête invalide. Vérifiez les informations saisies.";
    case 401: return "Votre session a expiré. Reconnectez-vous.";
    case 403: return "Vous n'avez pas l'autorisation pour cette action.";
    case 404: return "Élément introuvable. Il a peut-être été supprimé.";
    case 409: return "Ce contenu a été modifié entre temps. Rafraîchissez la page.";
    case 422: return "Le formulaire contient des champs invalides.";
    case 429: return "Trop de requêtes. Patientez quelques secondes.";
    case 500: return "Le serveur a rencontré un souci. Notre équipe a été notifiée.";
    case 502:
    case 503:
    case 504: return "Le serveur est temporairement indisponible. Retentez dans 30 secondes.";
    default: break;
  }

  // Generic timeout
  if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
    return "Le serveur met trop de temps à répondre. Réessayez.";
  }

  // Last resort
  if (typeof error.message === "string" && error.message.length < 200) {
    return error.message;
  }

  return "Une erreur est survenue. Réessayez ou contactez le support.";
}

/**
 * Returns true if the error is recoverable by retrying (network glitch, 5xx, timeout).
 */
export function isRetryable(error) {
  if (!error) return false;
  const code = error.response?.status;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!error.response) return true; // network/timeout
  return code === 502 || code === 503 || code === 504 || code === 429;
}

/**
 * Best-effort wrapper that calls an async function with retry+humanize.
 * Returns a tuple-style [data, error] without throwing.
 *
 *   const [lead, err] = await safe(() => leadsApi.get(id));
 *   if (err) toast.error(err);
 */
export async function safe(fn, { retries = 1, retryDelay = 800 } = {}) {
  let attempt = 0;
  while (true) {
    try {
      const data = await fn();
      return [data, null];
    } catch (e) {
      if (attempt < retries && isRetryable(e)) {
        attempt += 1;
        await new Promise((r) => setTimeout(r, retryDelay * attempt));
        continue;
      }
      return [null, humanizeError(e)];
    }
  }
}
